import { useCallback, useEffect, useRef, useState } from "react";
import { Undo2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui";

// =============================================================================
// BodySketch — circle where it hurts on an outline of the body (1 Nov 2026)
// =============================================================================
// James, 1 Nov 2026: "lets quickly add that body map while in incidents" and,
// on the first cut (tap-the-region figures): "looks a bit blocky … I am happy
// with red pen circle the areas." So it is the Hilux pattern: a black-and-
// white outline — the front and the back of a person, side by side — and the
// reporter circles the sore spot with a finger. Undo, Clear. Exports a PNG
// (exportBlob on the handle) that the form uploads to hse-evidence beside the
// photos, tagged "injury", so the incident page and the broker report show it
// with the rest of the evidence. The words (body-part chips, "what's hurt")
// stay — the register and the reports still need text.
//
// The outline is drawn here from a path, no image file. The figure's own left
// and right are marked under each view: on the FRONT their left is on your
// right.
// =============================================================================

export const DW = 700, DH = 520;
export interface BodySketchHandle { exportBlob: () => Promise<Blob | null>; isEmpty: () => boolean }

export function BodySketch({ strokes, onChange, title, handleRef }: {
  strokes: Array<Array<[number, number]>>;
  onChange: (s: Array<Array<[number, number]>>) => void;
  title: string;
  handleRef: React.MutableRefObject<BodySketchHandle | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [draft, setDraft] = useState<Array<[number, number]> | null>(null);

  const paint = useCallback((g: CanvasRenderingContext2D, forExport = false) => {
    g.clearRect(0, 0, DW, DH); g.fillStyle = "#fff"; g.fillRect(0, 0, DW, DH);
    drawBody(g);
    if (forExport) { g.fillStyle = "#333"; g.font = "600 16px sans-serif"; g.textAlign = "left"; g.textBaseline = "bottom"; g.fillText(title, 16, DH - 10); }
    const all = draft ? [...strokes, draft] : strokes;
    for (const s of all) {
      g.save(); g.strokeStyle = "#e0323a"; g.lineWidth = 6; g.lineCap = "round"; g.lineJoin = "round";
      g.beginPath(); s.forEach(([x, y], i) => i ? g.lineTo(x, y) : g.moveTo(x, y)); g.stroke(); g.restore();
    }
  }, [strokes, draft, title]);

  useEffect(() => { const cv = canvasRef.current; if (cv) paint(cv.getContext("2d")!); }, [paint]);
  useEffect(() => {
    handleRef.current = {
      isEmpty: () => strokes.length === 0,
      exportBlob: async () => { const cv = document.createElement("canvas"); cv.width = DW; cv.height = DH; paint(cv.getContext("2d")!, true); return new Promise(res => cv.toBlob(b => res(b), "image/png")); },
    };
  }, [paint, strokes.length, handleRef]);

  const toLogical = (e: React.PointerEvent) => { const r = canvasRef.current!.getBoundingClientRect(); return [((e.clientX - r.left) / r.width) * DW, ((e.clientY - r.top) / r.height) * DH] as [number, number]; };

  return (
    <div className="space-y-1.5">
      <canvas ref={canvasRef} width={DW} height={DH}
        onPointerDown={e => { e.preventDefault(); (e.target as HTMLElement).setPointerCapture(e.pointerId); setDraft([toLogical(e)]); }}
        onPointerMove={e => { if (!draft) return; e.preventDefault(); setDraft(d => d ? [...d, toLogical(e)] : d); }}
        onPointerUp={() => { if (draft && draft.length > 2) onChange([...strokes, draft]); setDraft(null); }}
        onPointerCancel={() => setDraft(null)}
        className="w-full rounded-md border border-line bg-white select-none" style={{ touchAction: "none", aspectRatio: `${DW} / ${DH}` }}/>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="secondary" icon={Undo2} disabled={strokes.length === 0} onClick={() => onChange(strokes.slice(0, -1))}>Undo</Button>
        <Button size="sm" variant="ghost" icon={Trash2} disabled={strokes.length === 0} onClick={() => onChange([])}>Clear</Button>
        <span className="text-[11px] text-text-muted ml-auto">Circle where it hurts with your finger</span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// the outline — a standing figure, front and back, black on white, with the
// muscle lines a safety form's body map usually carries, and the Coretel C on
// the right shin (James, 1 Nov: "where HVAC have the leg tattoo we could do a
// Coretel Group somewhere … even just the logo with the C but in our format").
// The silhouette is one smooth path through hand-placed points, mirrored.
// INTERIM: a proper illustrated figure (front / back / both sides, like the
// HVAC client's form) is an image asset to drop in here when James has one —
// swap drawBody for drawImage; nothing else changes.
// -----------------------------------------------------------------------------
// left-half outline, neck -> crotch; mirrored. units 200 x 410
const L: Array<[number, number]> = [[93,60],[91,72],[80,77],[66,83],[55,92],[47,106],[43,126],[41,148],[40,166],[38,188],[37,210],[36,226],[33,242],[32,258],[37,271],[48,273],[57,262],[59,244],[61,226],[63,206],[65,186],[67,166],[69,146],[71,124],[73,108],[76,136],[79,160],[75,185],[70,206],[66,232],[65,262],[67,290],[70,318],[71,345],[72,372],[66,388],[60,404],[96,406],[98,392],[95,374],[93,345],[94,318],[95,292],[96,262],[99,232],[100,220]];
const R: Array<[number, number]> = L.slice().reverse().map(([x, y]) => [200 - x, y]);
const P=L.concat(R.slice(1));
function cr(pts: Array<[number, number]>, t = 0.5): string {const n=pts.length;let d=`M ${pts[0][0]} ${pts[0][1]}`;for(let i=0;i<n;i++){const p0=pts[(i-1+n)%n],p1=pts[i],p2=pts[(i+1)%n],p3=pts[(i+2)%n];const c1: [number, number] = [p1[0]+(p2[0]-p0[0])*t/3,p1[1]+(p2[1]-p0[1])*t/3], c2: [number, number] = [p2[0]-(p3[0]-p1[0])*t/3,p2[1]-(p3[1]-p1[1])*t/3];d+=` C ${c1[0].toFixed(1)} ${c1[1].toFixed(1)} ${c2[0].toFixed(1)} ${c2[1].toFixed(1)} ${p2[0]} ${p2[1]}`;}return d+" Z";}
const BODY = cr(P);   // one closed path, both halves
function q(g: CanvasRenderingContext2D, pts: Array<[number, number]>) {g.beginPath();g.moveTo(pts[0][0],pts[0][1]);for(let i=1;i<pts.length-1;i+=2)g.quadraticCurveTo(pts[i][0],pts[i][1],pts[i+1][0],pts[i+1][1]);g.stroke();}
function mir(pts: Array<[number, number]>): Array<[number, number]> { return pts.map(([x, y]) => [200 - x, y]); }
function both(g: CanvasRenderingContext2D, pts: Array<[number, number]>) {q(g,pts);q(g,mir(pts));}
function figure(g: CanvasRenderingContext2D, ox: number, oy: number, k: number, view: "front" | "back") {
  g.save();g.translate(ox,oy);g.scale(k,k);g.lineJoin="round";g.lineCap="round";
  g.lineWidth=2.2/k;g.strokeStyle="#111";g.fillStyle="#fff";
  g.beginPath();g.ellipse(100,34,22,27,0,0,Math.PI*2);g.fill();g.stroke();
  const p=new Path2D(BODY);g.fill(p);g.stroke(p);
  // fingers (both hands): three short lines from the knuckles
  g.lineWidth=1.3/k;g.strokeStyle="#333";
  for(const s of [1,-1]){const f = (x: number) => s === 1 ? x : 200 - x;for(const x of [38,44,50]){g.beginPath();g.moveTo(f(x),258);g.lineTo(f(x)-s*1,271);g.stroke();}
    g.beginPath();g.moveTo(f(56),246);g.quadraticCurveTo(f(52),238,f(57),232);g.stroke();} // thumb
  g.strokeStyle="#444";
  if(view==="front"){
    // face
    for(const x of [92,108]){g.beginPath();g.ellipse(x,30,3,1.8,0,0,Math.PI*2);g.stroke();}
    g.beginPath();g.moveTo(100,34);g.lineTo(98,42);g.lineTo(102,42);g.stroke();
    g.beginPath();g.moveTo(94,49);g.quadraticCurveTo(100,53,106,49);g.stroke();
    g.beginPath();g.moveTo(84,22);g.quadraticCurveTo(100,8,116,22);g.stroke(); // hairline
    // traps + collarbones
    both(g,[[91,72],[84,84],[66,83]]);
    both(g,[[98,88],[86,86],[72,92]]);
    // pecs
    both(g,[[72,96],[70,126],[98,124]]);
    g.beginPath();g.moveTo(100,92);g.lineTo(100,124);g.stroke();
    // deltoid + biceps
    both(g,[[66,84],[52,100],[56,118]]);
    both(g,[[56,120],[48,150],[58,164]]);
    both(g,[[62,170],[52,190],[57,222]]); // forearm
    // abs
    g.beginPath();g.moveTo(100,126);g.lineTo(100,192);g.stroke();
    for(const y of [142,158,174]){both(g,[[90,y],[95,y-2],[100,y]]);}
    both(g,[[88,128],[85,160],[90,192]]); // rectus edge
    both(g,[[76,140],[80,176],[72,200]]); // obliques
    // quads, knees, shins
    both(g,[[80,222],[86,262],[82,286]]);
    both(g,[[70,226],[66,262],[72,284]]);
    for(const x of [81,119]){g.beginPath();g.ellipse(x,300,7,9,0,0,Math.PI*2);g.stroke();}
    both(g,[[84,312],[88,340],[84,370]]);
  }else{
    g.beginPath();g.moveTo(80,32);g.quadraticCurveTo(100,10,120,32);g.stroke(); // hair
    both(g,[[91,72],[88,90],[72,100]]); // traps
    g.beginPath();g.moveTo(100,80);g.lineTo(100,205);g.stroke(); // spine
    both(g,[[72,100],[90,112],[84,136]]); // scapula
    both(g,[[78,140],[90,170],[82,196]]); // lats
    both(g,[[68,84],[52,100],[56,118]]); // rear delt
    both(g,[[56,120],[48,150],[58,164]]); // triceps
    both(g,[[62,170],[52,190],[57,222]]);
    both(g,[[72,200],[84,214],[100,210]]); // glute
    both(g,[[72,228],[76,262],[72,288]]); // hamstring
    for(const x of [81,119]){g.beginPath();g.moveTo(x-7,300);g.lineTo(x+7,300);g.stroke();}
    both(g,[[72,310],[64,336],[72,366]]); // calves
    both(g,[[90,312],[96,336],[90,366]]);
  }
  g.restore();
}
function mark(g: CanvasRenderingContext2D, ox: number, oy: number, k: number) {
  // the Coretel C on the right shin — subtle, like a small tattoo (James: "the logo would need to be subtle")
  g.save(); g.translate(ox, oy); g.scale(k, k);
  g.translate(74, 340); const m = 0.016; g.scale(m, m); g.translate(-534, -540); g.rotate(-0.12);
  const c = new Path2D("M30 30H1038L870 198H198V882H870L1038 1050H30Z");
  g.strokeStyle = "#9a9a9a"; g.lineWidth = 48; g.lineJoin = "miter"; g.stroke(c);
  g.restore();
}
function label(g: CanvasRenderingContext2D, t: string, x: number, y: number, size = 14, color = "#555") {g.fillStyle=color;g.font=`${size}px sans-serif`;g.textAlign="center";g.textBaseline="middle";g.fillText(t,x,y);}
export function drawBody(g: CanvasRenderingContext2D) {const k=1.05,fx=115,bx=385,oy=18;figure(g,fx,oy,k,"front");figure(g,bx,oy,k,"back");mark(g,fx,oy,k);
 label(g,"Front",fx+100*k,DH-42,15,"#333");label(g,"Back",bx+100*k,DH-42,15,"#333");
 label(g,"R",fx+20,DH-62,12,"#888");label(g,"L",fx+200*k-20,DH-62,12,"#888");label(g,"L",bx+20,DH-62,12,"#888");label(g,"R",bx+200*k-20,DH-62,12,"#888");}
